import React, { useCallback, useMemo } from 'react';
import { llm } from '@cotera/api';
import { useWhoami } from '@cotera/client/app/stores/org';
import { useGatedModelFilter } from '@cotera/client/app/hooks/use-gated-models';
import {
  ModelPickerMenu,
  type ModelMenuGroup,
  type ModelMenuItem,
} from '@cotera/client/app/components/model-picker-menu';

/**
 * Models that must never be offered in the agent model dropdown, even when they
 * are otherwise eligible (categorized in the registry, or present in an org's
 * `ORG_MODEL_ALLOWLIST` entry). Every other model stays selectable — add an id
 * here to take one out of the picker.
 *
 * `azure-gpt-5.5-subsidized` is billed below cost and exists to back Coco's own
 * defaults, so agents must not be newly pointed at it from the UI — its
 * full-price twin `azure-gpt-5.5` is offered instead, under the same label.
 * Agents already saved on an excluded model keep it: it renders under the
 * "Current" group, labelled with the registry `name` so the subsidy is visible
 * rather than masked by the shared `displayLabel`.
 */
const EXCLUDED_MODEL_IDS: ReadonlySet<string> = new Set<llm.ModelId>([
  llm.LlmModels.azureFoundry.AzureGpt5Dot5Subsidized,
]);

const isSelectableModel = (id: string): boolean => !EXCLUDED_MODEL_IDS.has(id);

const toModelMenuItem = (id: string): ModelMenuItem => {
  const meta = llm.getModel(id);
  return {
    id,
    label: meta?.displayLabel ?? meta?.name ?? id,
    creditMultiplier: meta?.creditMultiplier ?? 0,
  };
};

export const CategorizedModelSelector: React.FC<{
  model: string;
  onModelChange: (model: string) => void;
}> = ({ model, onModelChange }) => {
  const org = useWhoami((s) => s.org);
  const allowlist = llm.orgModelAllowlist(org.id);

  // Some model choices are hidden behind a WorkOS feature flag until an org is
  // opted in. Fold that flag gate together with the static exclusion list into a
  // single "is this model shown" predicate so list building and the "Current"
  // fallback below stay in agreement.
  const passesFlagGate = useGatedModelFilter();
  const isVisibleModel = useCallback(
    (id: string): boolean => isSelectableModel(id) && passesFlagGate(id),
    [passesFlagGate]
  );

  // Restricted orgs render their full allowed list, minus any excluded ids.
  // Display metadata lives on the model registry, so we source it from there.
  const allowedModels = useMemo(() => {
    if (allowlist === undefined) {
      return null;
    }
    return llm.groupLegacyModelsByVendor(
      allowlist.filter((id) => isVisibleModel(id)).map(toModelMenuItem)
    );
  }, [allowlist, isVisibleModel]);

  const modelsByCategory = useMemo(() => {
    const map = new Map<llm.ModelCategory, ModelMenuItem[]>();
    for (const m of llm.CATEGORIZED_MODELS) {
      if (!isVisibleModel(m.id) || llm.isLegacyModel(m.id)) {
        continue;
      }
      const existing = map.get(m.category) ?? [];
      existing.push(toModelMenuItem(m.id));
      map.set(m.category, existing);
    }
    return map;
  }, [isVisibleModel]);

  // Superseded versions stay selectable under "Other models", by vendor.
  const legacyByVendor = useMemo(
    () =>
      llm.groupLegacyModelsByVendor(
        Object.values(llm.MODELS)
          .filter((m) => m.legacy === true && isVisibleModel(m.id))
          .map((m) => toModelMenuItem(m.id))
      ).legacyByVendor,
    [isVisibleModel]
  );

  const categories = llm.MODEL_CATEGORIES;

  const currentMeta = useMemo(() => llm.getModel(model), [model]);

  const displayValue = useMemo(() => {
    // An excluded model can only ever be an agent's already-saved selection, so
    // it shows the registry `name` — which spells out "subsidized" — instead of
    // the `displayLabel` it shares with the full-price GPT 5.5 entry. Otherwise
    // two different models would read identically in the UI.
    if (!isSelectableModel(model)) {
      return currentMeta?.name ?? model;
    }
    return currentMeta?.displayLabel ?? currentMeta?.name ?? model;
  }, [model, currentMeta]);

  const otherGroups =
    allowedModels !== null ? allowedModels.legacyByVendor : legacyByVendor;

  // Whether the branch below already renders an item for the selected model.
  // False for excluded models (filtered out of every list) and for models
  // outside a restricted org's allowlist.
  const currentModelRendered = useMemo(() => {
    const rendered = [
      ...(allowedModels !== null
        ? allowedModels.current
        : [...modelsByCategory.values()].flat()),
      ...otherGroups.flatMap((g) => g.models),
    ];
    return rendered.some((m) => m.id === model);
  }, [allowedModels, modelsByCategory, otherGroups, model]);

  const currentGroup: ModelMenuGroup[] =
    !currentModelRendered && currentMeta !== undefined
      ? [
          {
            // Keeps an agent saved on a model the picker no longer offers from
            // silently losing its selected item.
            key: 'current',
            label: 'Current',
            models: [
              {
                id: model,
                label: displayValue,
                creditMultiplier: currentMeta.creditMultiplier,
              },
            ],
          },
        ]
      : [];

  return (
    <ModelPickerMenu
      value={model}
      valueLabel={displayValue}
      onChange={onModelChange}
      groups={[
        ...currentGroup,
        ...(allowedModels !== null
          ? [{ key: 'models', label: 'Models', models: allowedModels.current }]
          : categories.map((category) => ({
              key: category,
              label: llm.getCategoryLabel(category),
              models: modelsByCategory.get(category) ?? [],
            }))),
      ]}
      otherGroups={otherGroups.map(({ vendor, models }) => ({
        key: vendor,
        label: vendor,
        models,
      }))}
    />
  );
};
